import { Component, Inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule, MatChipInputEvent } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule, MatSnackBarRef } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Observable, map, startWith } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

import { ArticleListItem, ArticleHistoryResponse, BatchCreateDraftsResponse } from '../../../../core/models/knowledge-base.model';

import { LucideAngularModule, ClipboardIcon, Edit, Plus, FileText, History, MoveRight, File, Info, CornerDownRight,
PackageCheck, Star, BadgeCheck, OctagonAlert, StarOff, PackageX, Upload, TriangleAlert, X, Trash2 } from "lucide-angular";
import { KnowledgeBaseService } from '../../../../core/services/knowledge-base.service';
import { ArticlePublishConfirmModalComponent, ArticlePublishConfirmModalData } from '../article-publish-confirm/article-publish-confirm-modal.component';
import { RetrievalTestModalComponent } from '../retrieval-test/retrieval-test-modal.component';
export interface ArticleEditModalData {
    knowledgeBaseId: number;
    kbName?: string;
    article: ArticleListItem | null; // Si es null = modo creación, si tiene datos = modo edición
    picklists: {
        types: any[];
        items: { [typeId: number]: any[] };
    };
    isReadOnly?: boolean; // Modo solo lectura para Producción y QA
    articleHistory?: ArticleHistoryResponse; // Track precargado para evitar llamada HTTP
}

@Component({
    selector: 'app-article-edit-modal',
    standalone: true,
    imports: [ CommonModule, FormsModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule,
      MatCheckboxModule, MatChipsModule, MatDividerModule, MatProgressSpinnerModule, MatListModule, MatSnackBarModule,
      MatSelectModule, LucideAngularModule, MatAutocompleteModule, MatTooltipModule ],
    templateUrl: './article-edit-modal.component.html',
    styleUrls: ['./article-edit-modal.component.scss']
})
export class ArticleEditModalComponent implements OnInit{
    articleForm: FormGroup | null = null;
    isEditMode: boolean = false;
    isReadOnly: boolean = false;
    saving: boolean = false;
    publishing: boolean = false;
    loadingHistory = signal<boolean>(false);
    articleHistory = signal<ArticleHistoryResponse | null>(null);
    originalArticleData: Partial<ArticleListItem> | null = null;
    loadingSnackBarRef: MatSnackBarRef<any> | null = null;

    // Keywords chips
    keywords = signal<string[]>([]);
    
    // Word counter for content field
    contentWordCount = signal<number>(0);
    readonly separatorKeysCodes = [ENTER, COMMA] as const;

    // Picklists para los selectores
    picklistTypes: any[] = [];
    picklistItemsByType: { [typeId: number]: any[] } = {}; // Solo items activos para autocompletes
    allPicklistItemsByType: { [typeId: number]: any[] } = {}; // Todos los items (activos e inactivos) para visualización

    // Autocomplete controls y filtered options
    autocompleteControls: { [key: string]: FormControl } = {};
    filteredOptions: { [key: string]: Observable<any[]> } = {};

    // icons
    readonly Clipboard = ClipboardIcon;
    readonly Edit = Edit;
    readonly Plus = Plus;
    readonly FileText = FileText;
    readonly History = History;
    readonly MoveRight = MoveRight;
    readonly File = File;
    readonly Info = Info;
    readonly CornerDownRight = CornerDownRight;
    readonly Upload = Upload;
    readonly TriangleAlert = TriangleAlert;
    readonly X = X;


    constructor(
      public dialogRef: MatDialogRef<ArticleEditModalComponent>,
      @Inject(MAT_DIALOG_DATA) public data: ArticleEditModalData,
      private readonly fb: FormBuilder,
      private readonly knowledgeBaseService: KnowledgeBaseService,
      private readonly snackBar: MatSnackBar,
      private readonly dialog: MatDialog,
      private readonly router: Router,
      private readonly route: ActivatedRoute
    ) {
      // Establecer el modo de edición automáticamente basado en si hay artículo
      this.isEditMode = !!data.article;
      
      // Establecer el modo solo lectura
      this.isReadOnly = !!data.isReadOnly;
      
      // Cargar los picklists y sus items
      if (data.picklists) {
        this.picklistTypes = data.picklists.types;
        this.picklistItemsByType = {};
        this.allPicklistItemsByType = {};
        for (const typeId in data.picklists.items) {
            // Filtrar solo activos para los autocompletes
            this.picklistItemsByType[typeId] = data.picklists.items[typeId].filter(item => item.isActive);
            // Guardar todos los items (activos e inactivos) para visualización
            this.allPicklistItemsByType[typeId] = data.picklists.items[typeId];
        }
      }
    }

    ngOnInit(): void {
      this.loadingHistory.set(true);
      this.initForm();
      this.initAutocompletes();
      this.initKeywords();
      // Si ya tenemos el historial precargado, usarlo directamente
      if (this.data.articleHistory) {
        this.articleHistory.set(this.data.articleHistory);
        this.loadingHistory.set(false);
      } else {
        this.loadHistory();
      }
    }

    /**
     * Inicializa los controles de autocomplete para los campos de picklist, solo si no estamos en modo solo lectura
     */
    private initAutocompletes(): void {
      if (!this.isReadOnly) {
        this.setupAutocomplete('requestTypePicklistItemId', 'tipo');
        this.setupAutocomplete('category1PicklistItemId', 'categoria_1');
        this.setupAutocomplete('category2SalesforcePicklistItemId', 'categoria_2_salesforce');
        this.setupAutocomplete('category2PicklistItemId', 'categoria_2');
        this.setupAutocomplete('category1SalesforcePicklistItemId', 'categoria_1_salesforce');
        this.setupAutocomplete('actionsPicklistItemId', 'acciones');
        this.setupAutocomplete('businessPicklistItemId', 'id_solicitud');
      }
    }

    /**
     * Helper para obtener items de un tipo específico de picklist
     */
    getPicklistItems(typeId: number): any[] {
      return this.picklistItemsByType[typeId] || [];
    }

    /**
     * Inicializa un control de autocomplete para un campo de picklist
     */
    setupAutocomplete(fieldName: string, picklistTypeName: string): void {
      const typeId = this.getPicklistTypeByName(picklistTypeName)?.picklistTypeId;
      if (!typeId) return;

      // Crear FormControl para el input de búsqueda
      // Solo estos campos deben marcarse como obligatorios en el autocomplete
      const requiredFields = [
        'requestTypePicklistItemId',
        'category1PicklistItemId',
        'category2PicklistItemId',
      ];
      const isRequired = requiredFields.includes(fieldName);
      const control = new FormControl('', isRequired ? Validators.required : null);
      this.autocompleteControls[fieldName] = control;

      // Configurar el valor inicial si hay un valor en el formulario
      const currentValue = this.articleForm?.get(fieldName)?.value;
      if (currentValue) {
        const item = this.getPicklistItems(typeId).find(i => i.picklistItemId === currentValue);
        if (item) {
          control.setValue(item.name);
        }
      }

      // Crear observable de opciones filtradas
      this.filteredOptions[fieldName] = control.valueChanges.pipe(
        startWith(''),
        map(value => this.filterPicklistItems(value || '', typeId))
      );
    }

    /**
     * Filtra los items de picklist basado en el texto de búsqueda
     */
    private filterPicklistItems(value: string, typeId: number): any[] {
      const items = this.getPicklistItems(typeId);
      if (!value || typeof value !== 'string') {
        return items;
      }
      const filterValue = value.toLowerCase();
      return items.filter(item => 
        item.name.toLowerCase().includes(filterValue) || 
        item.code?.toLowerCase().includes(filterValue)
      );
    }

    /**
     * Función para mostrar el nombre del item en el input
     */
    displayFn(item: any): string {
      return item?.name || '';
    }

    /**
     * Maneja la selección de un item del autocomplete
     */
    onAutocompleteSelected(fieldName: string, event: any): void {
      const selectedItem = event.option.value;
      if (selectedItem && this.articleForm) {
        this.articleForm.get(fieldName)?.setValue(selectedItem.picklistItemId);
        this.autocompleteControls[fieldName].setValue(selectedItem.name);
        
        // Si es el campo de acciones, verificar si se seleccionó TRANSFER_TO_CASE_AGENT
        if (fieldName === 'actionsPicklistItemId') {
          this.checkTransferToCaseAgentValidation(selectedItem);
        }
      }
    }
    
    /**
     * Verifica si la acción seleccionada es TRANSFER_TO_CASE_AGENT y marca el campo
     * businessPicklistItemId como tocado para mostrar el error si está vacío
     */
    private checkTransferToCaseAgentValidation(selectedAction: any): void {
      if (selectedAction?.code === 'TRANSFER_TO_CASE_AGENT' || selectedAction?.name === 'TRANSFER_TO_CASE_AGENT') {
        // Marcar el campo como tocado para mostrar el error si está vacío
        const businessControl = this.articleForm?.get('businessPicklistItemId');
        if (businessControl && !businessControl.value) {
          businessControl.markAsTouched();
        }
      }
    }

    /**
     * Limpia el campo de autocomplete
     */
    clearAutocomplete(fieldName: string): void {
      this.autocompleteControls[fieldName]?.setValue('');
      this.articleForm?.get(fieldName)?.setValue(null);

      if (fieldName === 'category1PicklistItemId') {
        this.syncIdentifierWithCategory1();
      }
      
      // Si es el campo de businessPicklistItemId y TRANSFER_TO_CASE_AGENT está seleccionado,
      // marcar como tocado para mostrar el error
      if (fieldName === 'businessPicklistItemId' && this.isTransferToCaseAgentSelected()) {
        this.articleForm?.get(fieldName)?.markAsTouched();
      }
    }

    /**
     * Helper para buscar un tipo de picklist por nombre
     */
    getPicklistTypeByName(name: string): any | undefined {
        return this.picklistTypes.find(type => type.name === name);
    }

    /**
     * Validador personalizado que verifica si cuando la acción es TRANSFER_TO_CASE_AGENT,
     * el campo businessPicklistItemId (ID de solicitud) también esté completo
     */
    private transferToCaseAgentValidator(): any {
      return (formGroup: FormGroup) => {
        const actionsPicklistItemId = formGroup.get('actionsPicklistItemId')?.value;
        const businessPicklistItemId = formGroup.get('businessPicklistItemId')?.value;
        
        if (!actionsPicklistItemId) {
          return null; // No validar si no hay acción seleccionada
        }
        
        // Buscar el nombre de la acción seleccionada
        const actionsTypeId = this.getPicklistTypeByName('acciones')?.picklistTypeId;
        if (!actionsTypeId) {
          return null;
        }
        
        const actionItems = this.getPicklistItems(actionsTypeId);
        const selectedAction = actionItems.find(item => item.picklistItemId === actionsPicklistItemId);
        
        // Si la acción es TRANSFER_TO_CASE_AGENT y no hay businessPicklistItemId
        if (selectedAction?.code === 'TRANSFER_TO_CASE_AGENT' || selectedAction?.name === 'TRANSFER_TO_CASE_AGENT') {
          if (!businessPicklistItemId) {
            return { transferToCaseAgentRequiresBusinessId: true };
          }
        }
        
        return null;
      };
    }

    /**
     * Validador personalizado que verifica si el contenido tiene al menos 40 palabras
     */
    private minWordsValidator(minWords: number) {
      return (control: FormControl) => {
        if (!control.value) {
          return null; // Si está vacío, el validador required se encargará
        }
        const wordCount = this.countWords(control.value);
        return wordCount < minWords ? { minWords: { required: minWords, actual: wordCount } } : null;
      };
    }

    /**
     * Verifica si el formulario tiene el error de validación de TRANSFER_TO_CASE_AGENT
     * y si el campo de ID de solicitud ha sido tocado
     */
    hasTransferToCaseAgentError(): boolean {
      const hasError = this.articleForm?.hasError('transferToCaseAgentRequiresBusinessId') || false;
      const businessControl = this.articleForm?.get('businessPicklistItemId');
      const isTouched = businessControl?.touched || false;
      return hasError && isTouched;
    }

    /**
     * Verifica si la acción seleccionada actualmente es TRANSFER_TO_CASE_AGENT
     */
    isTransferToCaseAgentSelected(): boolean {
      const actionsPicklistItemId = this.articleForm?.get('actionsPicklistItemId')?.value;
      if (!actionsPicklistItemId) {
        return false;
      }
      
      const actionsTypeId = this.getPicklistTypeByName('acciones')?.picklistTypeId;
      if (!actionsTypeId) {
        return false;
      }
      
      const actionItems = this.getPicklistItems(actionsTypeId);
      const selectedAction = actionItems.find(item => item.picklistItemId === actionsPicklistItemId);
      
      return selectedAction?.code === 'TRANSFER_TO_CASE_AGENT' || selectedAction?.name === 'TRANSFER_TO_CASE_AGENT';
    }

    /**
     * Verifica si una acción específica (por ID) es TRANSFER_TO_CASE_AGENT
     */
    isTransferToCaseAgentAction(actionsPicklistItemId: number | undefined): boolean {
      if (!actionsPicklistItemId) {
        return false;
      }
      
      const actionsTypeId = this.getPicklistTypeByName('acciones')?.picklistTypeId;
      if (!actionsTypeId) {
        return false;
      }
      
      const actionItems = this.getPicklistItems(actionsTypeId);
      const selectedAction = actionItems.find(item => item.picklistItemId === actionsPicklistItemId);
      
      return selectedAction?.code === 'TRANSFER_TO_CASE_AGENT' || selectedAction?.name === 'TRANSFER_TO_CASE_AGENT';
    }

    /**
     * Inicializa el control de keywords como chips, separando por comas, y configurando el valor inicial si estamos en modo edición
     */
    private initForm(): void {
      if (this.isEditMode && this.data.article) {
        // Modo edición: inicializar con datos del artículo
        this.articleForm = this.fb.group({
          identifier : [this.data.article.identifier],
          category1PicklistItemId : [this.data.article.category1PicklistItemId, Validators.required],
          category2PicklistItemId : [this.data.article.category2PicklistItemId, Validators.required],
          category1SalesforcePicklistItemId : [this.data.article.category1SalesforcePicklistItemId],
          category2SalesforcePicklistItemId : [this.data.article.category2SalesforcePicklistItemId],
          actionsPicklistItemId : [this.data.article.actionsPicklistItemId],
          requestTypePicklistItemId : [this.data.article.requestTypePicklistItemId, Validators.required], // tipo
          businessPicklistItemId : [this.data.article.businessPicklistItemId], // id de solicitud
          isActive : [this.data.article.isActive],
          startDate : [this.data.article.startDate],
          question : [this.data.article.question, Validators.required],
          content : [this.data.article.content, Validators.required],
          keywords : [this.data.article.keywords],
          protocol : [this.data.article.protocol],
        }, { validators: this.transferToCaseAgentValidator() });
        
        // Store original values for change detection
        this.originalArticleData = { ...this.articleForm.value };
      } else {
        // Modo creación: formulario vacío
        // Asignar fecha actual a startDate usando fecha local
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const localDate = `${year}-${month}-${day}`;
        this.articleForm = this.fb.group({
          identifier : [''],
          category1PicklistItemId : ['', Validators.required],
          category2PicklistItemId : ['', Validators.required],
          category1SalesforcePicklistItemId : [''],
          category2SalesforcePicklistItemId : [''],
          actionsPicklistItemId : [''],
          requestTypePicklistItemId : ['', Validators.required], // tipo
          businessPicklistItemId : [''], // id de solicitud
          isActive : [true],
          startDate : [localDate],
          question : ['', Validators.required],
          content : ['', Validators.required],
          keywords : [''],
          protocol : [''],
        }, { validators: this.transferToCaseAgentValidator() });
      }
      
      // Escuchar cambios en actionsPicklistItemId para revalidar el formulario
      this.articleForm?.get('actionsPicklistItemId')?.valueChanges.subscribe(() => {
        const businessControl = this.articleForm?.get('businessPicklistItemId');
        
        // Si ya no es TRANSFER_TO_CASE_AGENT, limpiar el estado touched del campo businessPicklistItemId
        if (!this.isTransferToCaseAgentSelected() && businessControl?.touched) {
          businessControl.markAsUntouched();
        }
        
        this.articleForm?.updateValueAndValidity();
      });
      
      // Escuchar cambios en businessPicklistItemId para revalidar el formulario cuando se completa
      this.articleForm?.get('businessPicklistItemId')?.valueChanges.subscribe(() => {
        if (this.isTransferToCaseAgentSelected()) {
          this.articleForm?.updateValueAndValidity();
        }
      });

      // En creación, el identificador depende del prefijo de Categoría 1
      this.articleForm?.get('category1PicklistItemId')?.valueChanges.subscribe(() => {
        this.syncIdentifierWithCategory1();
      });
      
      // Escuchar cambios en el campo content para actualizar el contador de palabras
      this.articleForm?.get('content')?.valueChanges.subscribe((value) => {
        this.contentWordCount.set(this.countWords(value || ''));
      });
      
      // Inicializar el contador de palabras con el valor actual
      const currentContent = this.articleForm?.get('content')?.value;
      this.contentWordCount.set(this.countWords(currentContent || ''));

      this.syncIdentifierWithCategory1();
    }

    private syncIdentifierWithCategory1(): void {
      if (this.isEditMode || !this.articleForm) {
        return;
      }

      this.articleForm.get('identifier')?.setValue(this.getGeneratedIdentifierPreview(), { emitEvent: false });
    }

    private getCategory1Prefix(): string | null {
      const category1Id = this.articleForm?.get('category1PicklistItemId')?.value;
      if (!category1Id) {
        return null;
      }

      const category1Item = this.getPicklistItem(category1Id);
      if (!category1Item) {
        return null;
      }

      return category1Item.value || null;
    }

    getGeneratedIdentifierPreview(): string {
      const prefix = this.getCategory1Prefix();
      return prefix ? `${prefix}_XXX` : '';
    }

    getEditableIdentifierDisplay(): string {
      return this.getGeneratedIdentifierPreview() || 'Depende de Categoría 1...';
    }

    isIdentifierPreviewPlaceholder(): boolean {
      return !this.getGeneratedIdentifierPreview();
    }

    /**
     * Cuenta las palabras en un texto
     */
    countWords(text: string): number {
      if (!text || text.trim().length === 0) {
        return 0;
      }
      // Eliminar espacios múltiples y contar palabras separadas por espacios
      return text.trim().split(/\s+/).length;
    }

    /**
     * Carga el historial de un artículo
     */
    private loadHistory(): void {
      if (this.isEditMode && this.data.article) {
        this.knowledgeBaseService.getArticleHistory(this.data.article.knowledgeArticleId).subscribe({
          next: (history) => {
            this.articleHistory.set(history);
            // Aquí podrías almacenar el historial en una variable de estado si es necesario
            this.loadingHistory.set(false);
          },
          error: (error) => {
            console.error('Error loading article history:', error);
            this.loadingHistory.set(false);
          }
        });
      }
    }

    /**
     * Guarda el artículo, ya sea creando uno nuevo o actualizando el existente, y maneja la respuesta para mostrar 
     * mensajes adecuados al usuario. En modo edición, solo envía los campos que han cambiado.
     */
    checkSimilarityAndSave(): void {
      if (!this.articleForm?.valid || this.saving) return;

      const question = this.articleForm.get('question')?.value?.trim();
      if (!question) {
        this.saveArticle();
        return;
      }

      this.saving = true;
      this.knowledgeBaseService.testRetrieval(this.data.knowledgeBaseId, question, 'QA')
        .subscribe({
          next: (response) => {
            this.saving = false;
            const all = [...response.data.rerankedArticles, ...response.data.retrievedArticles];
            const hasSimilar = all.some(a => a.score > 0.8);
            if (hasSimilar) {
              const dialogRef = this.dialog.open(RetrievalTestModalComponent, {
                data: {
                  kbId: this.data.knowledgeBaseId,
                  kbName: this.data.kbName ?? '',
                  initialQuestion: question,
                  showProceedButton: true,
                },
                width: '900px',
                maxWidth: '95vw',
                height: '90vh',
                maxHeight: '95vh',
                panelClass: 'custom-dialog-container',
              });
              dialogRef.afterClosed().subscribe(result => {
                if (result?.proceed) this.saveArticle();
              });
            } else {
              this.saveArticle();
            }
          },
          error: () => {
            this.saving = false;
            this.saveArticle();
          },
        });
    }

    saveArticle(): void {
      if (!this.articleForm?.valid) {
        this.articleForm?.markAllAsTouched();

        // Verificar si el error es específico de TRANSFER_TO_CASE_AGENT
        if (this.hasTransferToCaseAgentError()) {
          this.snackBar.open(
            'Cuando la acción es TRANSFER_TO_CASE_AGENT, debe ingresar el ID de solicitud.',
            'Cerrar',
            { duration: 5000 }
          );
        }
        return;
      }

      this.saving = true;

      if (this.isEditMode && this.data.article) {
        // Actualizar artículo existente - enviar solo los cambios
        const changes = this.getChangedFields();
        
        if (Object.keys(changes).length === 0) {
          this.saving = false;
          this.snackBar.open('No hay cambios para guardar', 'Cerrar', { duration: 2000 });
          return;
        }
        
        this.knowledgeBaseService.updateArticle(this.data.article.knowledgeArticleId, changes).subscribe({
          next: (response: BatchCreateDraftsResponse) => {
            this.saving = false;
            this.processResponse(response, 'actualizado', 'actualizar');
          },
          error: (error) => {
            this.saving = false;
            this.snackBar.open('Error al actualizar el artículo. Por favor, inténtalo de nuevo. Error: ' + error.message, 'Cerrar', { duration: 5000 });
          }
        })
        
      } else {
        // Crear nuevo artículo
        this.knowledgeBaseService.createArticle(this.data.knowledgeBaseId, this.articleForm.value).subscribe({
          next: (response: BatchCreateDraftsResponse) => {
            this.saving = false;
            this.processResponse(response, 'creado', 'crear');
          },
          error: (error) => {
            console.error('Error creating article:', error);
            this.saving = false;
            this.snackBar.open('Error al crear el artículo. Por favor, inténtalo de nuevo.', 'Cerrar', { duration: 5000 });
          }
        });
      }
   }

    /**
     * Procesa la respuesta de creación/actualización para mostrar mensajes adecuados al usuario
     */
    private processResponse(response: BatchCreateDraftsResponse, action: string, failedAction: string): void {
      if (response.failed.length > 0) {
        const errorMessages = response.failed.map(failure => {
          // Para creación, failure tiene identifier
          // Para actualización, failure tiene articleId
          const identifier = (failure as any).identifier || `ID ${(failure as any).articleId}` || 'Desconocido';
          return `- ${identifier}: ${(failure as any).error}`;
        }).join('\n');
        this.snackBar.open(`No se pudo ${failedAction} el artículo:\n${errorMessages}`, 'Cerrar', { duration: 7000 });
      } 
      if (response.successful.length > 0) {
        this.snackBar.open(`El artículo fue ${action} correctamente.`, 'Cerrar', { duration: 3000 });
        this.dialogRef.close(true);
      }
    }

    /**
     * Helper para obtener el nombre de un picklist item por su ID (incluyendo items inactivos)
     */
    getPicklistItemName(itemId: number | undefined): string {
      if (!itemId) return '-';
      
      // Buscar en todos los items (activos e inactivos)
      for (const typeId in this.allPicklistItemsByType) {
        const items = this.allPicklistItemsByType[typeId];
        const found = items.find(item => item.picklistItemId === itemId);
        if (found) return found.name;
      }
      
      return '-';
    }

    /**
     * Helper para obtener el picklist item completo por su ID (incluyendo items inactivos)
     * Retorna el objeto completo para poder verificar si está activo o no
     */
    getPicklistItem(itemId: number | undefined): any {
      if (!itemId) return null;
      
      for (const typeId in this.allPicklistItemsByType) {
        const items = this.allPicklistItemsByType[typeId];
        const found = items.find(item => item.picklistItemId === itemId);
        if (found) return found;
      }
      
      return null;
    }

    /**
     * Detect which fields have changed from original values
     */
    private getChangedFields(): Partial<ArticleListItem> {
      if (!this.originalArticleData || !this.articleForm) {
        return {};
      }
      
      const currentValues = this.articleForm.value;
      const changes: Partial<ArticleListItem> = {};
      
      Object.keys(currentValues).forEach(key => {
        const originalValue = (this.originalArticleData as any)[key];
        const currentValue = currentValues[key];
        
        // Compare values - handle undefined, null, empty string cases
        if (this.hasValueChanged(originalValue, currentValue)) {
          changes[key as keyof ArticleListItem] = currentValue;
        }
      });
      
      return changes;
    }

    /**
     * Check if there are any changes in the form compared to original data
     */
    hasChanges(): boolean {
      return Object.keys(this.getChangedFields()).length > 0;
    }
  
    /**
     * Check if a value has changed, handling null/undefined/empty string cases
     */
    private hasValueChanged(original: any, current: any): boolean {
      // Normalize empty values
      const normalizedOriginal = original === null || original === undefined || original === '' ? null : original;
      const normalizedCurrent = current === null || current === undefined || current === '' ? null : current;
      
      return normalizedOriginal !== normalizedCurrent;
    }

    /**
     * Formatea una fecha en un formato legible para mostrar en el historial de versiones
     */
    formatDate(dateString: string | undefined): string {
      if (!dateString) return '-';
      
      // Si es solo una fecha (YYYY-MM-DD sin hora), tratarla como fecha local
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day); // mes es 0-indexed
        return date.toLocaleDateString('es-CL', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      // Si tiene timestamp completo, mostrar con hora
      const date = new Date(dateString);
      return date.toLocaleDateString('es-CL', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
      });
    }

    /**
     * Determina si estamos visualizando actualmente el borrador
     */
    isViewingDraft(): boolean {
      return this.data.article?.state === 'DRAFT';
    }

    /**
     * Determina si una versión QA específica es la que se está visualizando actualmente
     */
    isViewingQaVersion(qaRevisionId: number, qaVersionNumber: number): boolean {
      if (this.data.article?.state !== 'QA') return false;
      // Comparar por revisionId si está disponible, sino por versionNumber
      if (this.data.article.knowledgeArticleRevisionId) {
        return this.data.article.knowledgeArticleRevisionId === qaRevisionId;
      }
      return this.data.article.versionNumber === qaVersionNumber;
    }

    /**
     * Determina si una versión PROD específica es la que se está visualizando actualmente
     */
    isViewingProdVersion(prodRevisionId: number, prodVersionNumber: number): boolean {
      if (this.data.article?.state !== 'PROD') return false;
      // Comparar por revisionId si está disponible, sino por versionNumber
      if (this.data.article.knowledgeArticleRevisionId) {
        return this.data.article.knowledgeArticleRevisionId === prodRevisionId;
      }
      return this.data.article.versionNumber === prodVersionNumber;
    }

    /**
     * Determina si se está viendo la versión actual de QA
     */
    isViewingCurrentQa(): boolean {
      if (!this.data.article || this.data.article.state !== 'QA') return false;
      if (!this.articleHistory()) return false;
      
      const currentQaRevision = this.articleHistory()!.qaRevisions.find(qa => qa.isCurrentQa);
      if (!currentQaRevision) return false;
      
      // Verificar si la versión que estamos viendo es la actual de QA
      return this.isViewingQaVersion(currentQaRevision.qaRevisionId, currentQaRevision.qaVersionNumber);
    }

    /**
     * Determina si se está viendo la versión actual de PROD
     */
    isViewingCurrentProd(): boolean {
      if (!this.data.article || this.data.article.state !== 'PROD') return false;
      if (!this.articleHistory()) return false;
      
      // Buscar la revisión de producción actual
      for (const qaRevision of this.articleHistory()!.qaRevisions) {
        if (qaRevision.prodRevision?.isCurrentProd) {
          return this.isViewingProdVersion(
            qaRevision.prodRevision.prodRevisionId, 
            qaRevision.prodRevision.prodVersionNumber
          );
        }
      }
      return false;
    }

    /**
     * Maneja el click en una caja del historial para ver los detalles de esa versión
     */
    onHistoryBoxClick(environment: 'DRAFT' | 'QA' | 'PROD', version?: number): void {
      if (!this.data.article) return;

      // Si ya hay una navegación en curso (snackbar de carga visible), ignorar
      if (this.loadingSnackBarRef) {
        return;
      }

      // Verificar si ya estamos viendo esta versión
      const currentEnv = this.data.article.state;
      const currentVersion = this.data.article.versionNumber;
      
      // Si es DRAFT y hacemos click en DRAFT, no hacer nada
      if (environment === 'DRAFT' && currentEnv === 'DRAFT') {
        return;
      }
      
      // Si es QA o PROD con versión, verificar que no sea la misma
      if ((environment === 'QA' || environment === 'PROD') && version) {
        if (currentEnv === environment && currentVersion === version) {
          return;
        }
      }
      
      // Mostrar snackbar de carga 
      const versionText = version ? ` v${version}` : '';
      this.loadingSnackBarRef = this.snackBar.open(
        `Cargando artículo${versionText}...`,
        undefined,
        { duration: undefined }
      );
      
      // Navegar con query params (NO cerrar el modal todavía)
      const queryParams: any = {
        article: this.data.article.knowledgeArticleId,
        env: environment
      };
      
      if (version) {
        queryParams.version = version;
      }
      
      this.router.navigate([], {
        relativeTo: this.route.parent, // Navegar al nivel del componente padre (knowledge-base-page)
        queryParams: queryParams,
        queryParamsHandling: 'merge'
      });
    }

    /**      
     * Initialize keywords from article data
     */
    private initKeywords(): void {
      if (this.isEditMode && this.data.article?.keywords) {
        const keywordsArray = this.data.article.keywords
          .split(',')
          .map(k => k.trim())
          .filter(k => k.length > 0);
        this.keywords.set(keywordsArray);
      }
    }

    /**
     * Add a keyword chip
     */
    addKeyword(event: MatChipInputEvent): void {
      const value = (event.value || '').trim();
      if (value) {
        const currentKeywords = this.keywords();
        if (!currentKeywords.includes(value)) {
          this.keywords.set([...currentKeywords, value]);
          this.updateKeywordsFormControl();
        }
      }
      event.chipInput!.clear();
    }

    /**
     * Remove a keyword chip
     */
    removeKeyword(keyword: string): void {
      const currentKeywords = this.keywords();
      const index = currentKeywords.indexOf(keyword);
      if (index >= 0) {
        const newKeywords = [...currentKeywords];
        newKeywords.splice(index, 1);
        this.keywords.set(newKeywords);
        this.updateKeywordsFormControl();
      }
    }

    /**
     * Update the form control with the current keywords as comma-separated string
     */
    private updateKeywordsFormControl(): void {
      if (this.articleForm) {
        const keywordsString = this.keywords().join(',');
        this.articleForm.get('keywords')?.setValue(keywordsString);
      }
    }

    /**
     * Convert keywords string to array for display in readonly mode
     */
    getKeywordsArray(keywords: string | undefined): string[] {
      if (!keywords) return [];
      return keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0);
    }

    /**
     * Configuración para los íconos de estado en el historial de versiones, incluyendo el ícono a mostrar, tooltip,
     * clase CSS, si mostrar spinner de carga, y descripción para el tooltip detallado
     */
    private readonly STATUS_ICON_CONFIG: Record<string, { icon: any; tooltip: string; cssClass: string; showSpinner: boolean; description: string }> = {
      'CANDIDATE': {
        icon: PackageCheck,
        tooltip: 'Candidato',
        cssClass: 'w-5 h-5 text-blue-500 flex-shrink-0',
        showSpinner: false,
        description: 'Este artículo está en estado candidato y no está disponible para los bots.'
      },
      'SYNCHRONIZING': {
        icon: null,
        tooltip: 'Sincronizando',
        cssClass: '',
        showSpinner: true,
        description: 'Este artículo se está sincronizando, una vez finalizado el proceso, estará diponible para los bots'
      },
      'QA_SYNCHRONIZING': {
        icon: null,
        tooltip: 'Sincronizando a QA',
        cssClass: '',
        showSpinner: true,
        description: 'Este artículo se está sincronizando a QA, una vez finalizado el proceso, estará diponible en QA'
      },
      'PROD_SYNCHRONIZING': {
        icon: null,
        tooltip: 'Sincronizando a Producción',
        cssClass: '',
        showSpinner: true,
        description: 'Este artículo se está sincronizando a Producción, una vez finalizado el proceso, estará diponible en Producción'
      },
      'PUBLISHED:QA': {
        icon: Star,
        tooltip: 'Publicado',
        cssClass: 'w-5 h-5 text-orange-500 flex-shrink-0',
        showSpinner: false,
        description: 'Esta versión es la que utilizan los bots en QA, pero aún no está en Producción'

      },
      'QA_PUBLISHED': {
        icon: Star,
        tooltip: 'Publicado',
        cssClass: 'w-5 h-5 text-orange-500 flex-shrink-0',
        showSpinner: false,
        description: 'Esta versión es la que utilizan los bots en QA, pero aún no está en Producción'
      },
      'PROD_PUBLISHED': {
        icon: Star,
        tooltip: 'Publicado',
        cssClass: 'w-5 h-5 text-green-500 flex-shrink-0',
        showSpinner: false,
        description: 'Esta versión es la que utilizan los bots en Producción'
      },
      'PUBLISHED:PROD': {
        icon: Star,
        tooltip: 'Publicado',
        cssClass: 'w-5 h-5 text-green-500 flex-shrink-0',
        showSpinner: false,
        description: 'Esta versión es la que utilizan los bots en Producción'
      },
      'PROMOTED': {
        icon: BadgeCheck,
        tooltip: 'Promovido',
        cssClass: 'w-5 h-5 text-green-500 flex-shrink-0',
        showSpinner: false,
        description: 'Esta es la versión que utilizan los bots en QA y también en Producción',
      },
      'FAILED': {
        icon: OctagonAlert,
        tooltip: 'Fallido',
        cssClass: 'w-5 h-5 text-red-500 flex-shrink-0',
        showSpinner: false,
        description: 'Falló la sincronización de este artículo, no está disponible para los bots. Por favor revise el artículo e intente publicar nuevamente',
      },
      'SUPERSEDED:QA': {
        icon: StarOff,
        tooltip: 'Sustituido',
        cssClass: 'w-5 h-5 text-orange-500 flex-shrink-0',
        showSpinner: false,
        description: 'Esta versión fue sustituida por una más reciente, ya no está disponible para los bots en QA'
      },
      'SUPERSEDED:PROD': {
        icon: StarOff,
        tooltip: 'Sustituido',
        cssClass: 'w-5 h-5 flex-shrink-0',
        showSpinner: false,
        description: 'Esta versión fue sustituida por una más reciente, ya no está disponible para los bots en Producción'
      },
      'SUPERSEDED': {
        icon: StarOff,
        tooltip: 'Sustituido',
        cssClass: 'w-5 h-5 flex-shrink-0',
        showSpinner: false,
        description: 'Esta versión fue sustituida por una más reciente, ya no está disponible para los bots'
      },
      'DEPRECATED': {
        icon: PackageX,
        tooltip: 'Descartado',
        cssClass: 'w-5 h-5 flex-shrink-0',
        showSpinner: false,
        description: 'Esta versión fue descartada y no está disponible para los bots'
      }
    };

    /**
     * Determina si se debe mostrar el botón de publicar
     * Solo se muestra si el artículo existe y no está en ciertos estados
     */
    shouldShowPublishButton(): boolean {
      if (!this.data.article || this.isReadOnly) {
        return false;
      }
      
      if (!this.data.article.statusCode) {
        return true; // el artículo no ha sido publicado nunca pero si ha sido creado previamente
      }

      const blockedStatuses = [
        'CANDIDATE',
        'PROMOTED',
        'SYNCHRONIZING',
        'PUBLISHED:QA',
        'PUBLISHED:PROD',
        'QA_SYNCHRONIZING',
        'PROD_SYNCHRONIZING',
      ];
      
      return !blockedStatuses.includes(this.data.article.statusCode);
    }

    /**
     * Maneja la publicación del artículo, mostrando un modal de confirmación y luego procesando la respuesta 
     * para mostrar mensajes adecuados al usuario
     */
    publishArticle(): void {
      if (!this.data.article) {
        this.showError('No se encontró el artículo para publicar.');
        return;
      }
      
      const environment = this.data.article.state;
      const nextEnv = this.getNextEnvironmentLabel(environment);

      // Mostrar modal de confirmación
      const dialogRef = this.dialog.open(ArticlePublishConfirmModalComponent, {
        data: {
          articlesCount: 1,
          targetEnvironment: nextEnv,
          currentEnvironment: environment,
          articleIdentifiers: this.data.article.identifier ? [this.data.article.identifier] : []
        } as ArticlePublishConfirmModalData,
        width: '600px',
        maxWidth: '90vw',
        disableClose: false,
        panelClass: 'article-publish-confirm-dialog'
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (!confirmed || !this.data.article) {
          return;
        }

        // Proceder con la publicación
        this.publishing = true;

        let id = this.data.article.knowledgeArticleId;
        if (environment === 'QA' && this.data.article.knowledgeArticleRevisionId) {
          id = this.data.article.knowledgeArticleRevisionId;
        }
        
        this.knowledgeBaseService.publishArticles([id], environment)
          .subscribe({
            next: (response: BatchCreateDraftsResponse) => {
              this.publishing = false;
              this.processPublishResponse(response);
            },
            error: (error) => {
              this.publishing = false;
              console.error('Error al publicar artículo:', error);
              this.showError('Error al publicar el artículo. Por favor intente nuevamente.');
            }
          });
      });
    }

    /**
     * Procesa la respuesta de publicación y muestra mensajes apropiados
     */
    private processPublishResponse(response: BatchCreateDraftsResponse): void {
      // Mostrar errores si existen
      if (response.failed.length > 0) {
        this.showError(this.getPublishErrorMessage(response));
      }
      
      // Mostrar éxito si existen publicaciones exitosas
      if (response.successful.length > 0) {
        this.showSuccess(this.getPublishSuccessMessage(response));
      }
      
      // Solo cerrar el modal si todo fue exitoso (no hay errores)
      if (response.failed.length === 0 && response.successful.length > 0) {
        // Cerrar con el response para que el componente padre recargue los datos
        setTimeout(() => {
          this.dialogRef.close(response);
        }, 1500); // Pequeño delay para que el usuario vea el mensaje de éxito
      }
    }

    /**
     * Genera el mensaje de error para publicación
     */
    private getPublishErrorMessage(response: BatchCreateDraftsResponse): string {
      const errorMessages = response.failed.map(failure => `- ${failure.error}`).join('\n');
      return `No se pudo publicar el artículo:\n${errorMessages}`;
    }

    /**
     * Genera el mensaje de éxito para publicación
     */
    private getPublishSuccessMessage(response: BatchCreateDraftsResponse): string {
      const nextEnv = this.getNextEnvironmentLabel(this.data.article?.state || '');
      return `El artículo fue publicado correctamente a ${nextEnv}.`;
    }

    /**
     * Retorna el label del siguiente ambiente basado en el ambiente actual
     */
    private getNextEnvironmentLabel(currentState: string): string {
      switch (currentState) {
        case 'DRAFT':
        case 'Borrador':
          return 'QA';
        case 'QA':
          return 'Producción';
        case 'PROD':
        case 'Producción':
          return 'Producción';
        default:
          return 'el siguiente ambiente';
      }
    }

    private showSuccess(message: string): void {
      this.snackBar.open(message, 'Cerrar', {
        duration: 3000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: ['success-snackbar']
      });
    }

    private showError(message: string): void {
      this.snackBar.open(message, 'Cerrar', {
        duration: 5000,
        horizontalPosition: 'end',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
    }

    /**
     * Retorna la configuración de icono, tooltip y clases CSS basado en el statusCode y state
     */
    getStatusIconConfig(row: ArticleListItem): { icon: any; tooltip: string; cssClass: string; showSpinner: boolean, description: string } {
      if (row.isActive === false) {
        return {
          icon: Trash2,
          tooltip: 'Este artículo ha sido eliminado de todos los ambientes',
          cssClass: 'w-5 h-5 text-red-500 flex-shrink-0',
          showSpinner: false,
          description: 'Eliminado'
        };
      }

      const { statusCode, state } = row;
      
      if (!statusCode) {
        return { icon: null, tooltip: '', cssClass: '', showSpinner: false, description: '' };
      }
      
      // Intentar buscar con estado específico primero
      const keyWithState = `${statusCode}:${state}`;
      const config = this.STATUS_ICON_CONFIG[keyWithState] || this.STATUS_ICON_CONFIG[statusCode];
      
      // Retornar configuración encontrada o caso por defecto
      return config || {
        icon: null,
        tooltip: '',
        cssClass: '',
        showSpinner: false,
        description: ''
      };
    }

    /**
     * Obtiene la configuración de ícono basado solo en el statusCode
     * Útil para el historial donde no tenemos el objeto ArticleListItem completo
     */
    getStatusIconConfigByCode(statusCode: string | undefined): { icon: any; tooltip: string; cssClass: string; showSpinner: boolean } {
      if (!statusCode) {
        return { icon: null, tooltip: '', cssClass: '', showSpinner: false };
      }
      
      const config = this.STATUS_ICON_CONFIG[statusCode];
      
      return config ? {
        icon: config.icon,
        tooltip: config.tooltip,
        cssClass: config.cssClass,
        showSpinner: config.showSpinner
      } : {
        icon: null,
        tooltip: statusCode,
        cssClass: '',
        showSpinner: false
      };
    }

    /**
     * Busca artículos similares usando el endpoint de retrieval para detectar posibles duplicados.
     */
    checkSimilarArticles(): void {
      const question = this.articleForm?.get('question')?.value?.trim();
      if (!question) return;

      this.dialog.open(RetrievalTestModalComponent, {
        data: {
          kbId: this.data.knowledgeBaseId,
          kbName: 'Verificar duplicados',
          initialQuestion: question,
        },
        width: '900px',
        maxWidth: '95vw',
        height: '90vh',
        maxHeight: '95vh',
        panelClass: 'custom-dialog-container',
      });
    }
}
